import React, { useState, useEffect, useMemo } from 'react';
import SiteNav from '@/components/SiteNav';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Globe, Eye, EyeOff, Gift } from 'lucide-react';

const INTENT_KEY = 'ep_intent';
const FORM_KEY = 'ep_signup_form';
const MIN_PASSWORD = 8;

function readStoredIntent() {
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeIntent(intent) {
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent));
  } catch (_) { /* ignore */ }
  try {
    if (intent?.domain) sessionStorage.setItem('ep_preselected_domain', intent.domain);
  } catch (_) { /* ignore */ }
}

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  // Typed fields survive a refresh (sessionStorage only, never the password).
  const [form, setForm] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(sessionStorage.getItem(FORM_KEY) || '{}') || {}; } catch (_) { saved = {}; }
    return { full_name: saved.full_name || '', email: saved.email || '', password: '', business_name: saved.business_name || '', phone: saved.phone || '', referral_code: refCode || saved.referral_code || '' };
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const domainIntent = useMemo(() => {
    const fromQuery = {
      domain: (searchParams.get('domain') || '').trim(),
      price: searchParams.get('price') || '',
      available: searchParams.get('available'),
    };
    const stored = readStoredIntent() || {};
    const domain = fromQuery.domain || stored.domain || '';
    const price = fromQuery.price || stored.price || stored.website_price || '';
    if (!domain) return null;
    return {
      domain,
      price,
      website_price: price || stored.website_price,
      website_renew: stored.website_renew,
      available: fromQuery.available === 'unknown' || fromQuery.available == null
        ? stored.available
        : fromQuery.available !== 'false',
      is_premium: stored.is_premium,
      availability: stored.availability,
    };
  }, [searchParams]);

  useEffect(() => {
    if (refCode) setForm(p => ({ ...p, referral_code: refCode }));
  }, [refCode]);

  useEffect(() => {
    if (domainIntent?.domain) writeIntent({ ...(readStoredIntent() || {}), ...domainIntent });
  }, [domainIntent]);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // One box takes a friend's referral code or a coupon. Ask the server what it
  // is before promising anything — a coupon must not read as "$5 credit".
  const [codeCheck, setCodeCheck] = useState(null);
  useEffect(() => {
    const code = (form.referral_code || '').trim();
    if (!code) { setCodeCheck(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.checkSignupCode(code)
        .then(r => { if (!cancelled) setCodeCheck(r.data); })
        .catch(() => { if (!cancelled) setCodeCheck(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.referral_code]);

  useEffect(() => {
    const { password, ...keep } = form; // eslint-disable-line no-unused-vars
    try { sessionStorage.setItem(FORM_KEY, JSON.stringify(keep)); } catch (_) { /* ignore */ }
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < MIN_PASSWORD) { setError(`Password must be at least ${MIN_PASSWORD} characters`); return; }
    if (!agreed) { setError('Please agree to the Terms of Service and Privacy Policy to continue.'); return; }
    setLoading(true);
    try {
      await signup(form);
      try { sessionStorage.removeItem(FORM_KEY); } catch (_) { /* ignore */ }
      if (domainIntent?.domain) {
        writeIntent(domainIntent);
        const params = new URLSearchParams({
          start: 'checkout',
          domain: domainIntent.domain,
        });
        if (domainIntent.price) params.set('price', String(domainIntent.price));
        if (domainIntent.available != null) params.set('available', String(!!domainIntent.available));
        navigate(`/portal?${params.toString()}`);
      } else {
        navigate('/portal');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'We couldn\'t create your account. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const priceLabel = domainIntent?.price
    ? (String(domainIntent.price).startsWith('$') ? domainIntent.price : `$${domainIntent.price}`)
    : null;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6 pt-24 pb-12" data-testid="signup-page">
      <SiteNav />
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center min-h-[44px] text-sm text-slate-400 hover:text-white mb-2" data-testid="signup-home-link">&larr; Back to home</Link>
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 min-h-[44px]">
            <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>EasyPeazy</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Create Account</h1>
          <p className="text-slate-400">Get your website online today</p>
        </div>

        {domainIntent?.domain && (
          <div className="mb-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-4 py-3" data-testid="signup-domain-intent">
            <p className="text-sm text-indigo-200">
              You're claiming <strong className="text-white">{domainIntent.domain}</strong>
              {priceLabel ? <> — <span className="text-emerald-400 font-semibold">{priceLabel}/yr</span></> : null}
            </p>
            <p className="text-xs text-slate-400 mt-1">Create your account to finish checkout.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm" data-testid="signup-error">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name *</label>
            <input type="text" value={form.full_name} onChange={e => update('full_name', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="John Doe" required data-testid="signup-name-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Email *</label>
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="you@example.com" required data-testid="signup-email-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Password *</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 pr-12 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="At least 8 characters" minLength={MIN_PASSWORD} autoComplete="new-password" required data-testid="signup-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-slate-500 hover:text-slate-300" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Business Name</label>
            <input type="text" value={form.business_name} onChange={e => update('business_name', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="Optional" data-testid="signup-business-input" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone</label>
            <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="Optional" data-testid="signup-phone-input" />
          </div>

          {codeCheck?.valid && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-2" data-testid="signup-code-applied">
              <Gift className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-400">{codeCheck.message}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Coupon or Referral Code</label>
            <input type="text" value={form.referral_code} onChange={e => update('referral_code', e.target.value)} className="w-full bg-slate-900/50 border border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-lg h-11 px-4 text-slate-100 placeholder:text-slate-600 transition-all outline-none" placeholder="Optional" data-testid="signup-referral-input" />
            {codeCheck && !codeCheck.valid && codeCheck.message && (
              <p className="text-xs text-amber-400 mt-1.5" data-testid="signup-code-unknown">{codeCheck.message}</p>
            )}
          </div>

          <label className="flex items-start gap-2.5 text-sm text-slate-400 cursor-pointer select-none pt-1" data-testid="signup-consent">
            <span className="-ml-3 -mt-2.5 w-11 h-11 flex items-center justify-center flex-shrink-0"><input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-indigo-500 focus:ring-indigo-500 cursor-pointer flex-shrink-0" data-testid="signup-consent-checkbox" /></span>
            <span>
              I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">Privacy Policy</Link>.
            </span>
          </label>

          <button type="submit" disabled={loading || !agreed} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 rounded-lg font-medium transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20 mt-2" data-testid="signup-submit-btn">
            {loading ? 'Creating account...' : domainIntent?.domain ? 'Create Account & Continue' : 'Create Account'}
          </button>

          <p className="text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link
              to={domainIntent?.domain
                ? `/login?domain=${encodeURIComponent(domainIntent.domain)}${domainIntent.price ? `&price=${encodeURIComponent(domainIntent.price)}` : ''}&start=checkout`
                : '/login'}
              className="inline-flex items-center min-h-[44px] text-indigo-400 hover:text-indigo-300"
              data-testid="signup-login-link"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
